import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listApInvoices, listJournalEntries, listJournalLines } from '../../api/finance';
import { listSuppliers } from '../../api/masterData';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  ClickableRow,
  DashboardSkeleton,
  Drawer,
  DrawerField,
  DonutChart,
  EmptyState,
  ErrorNote,
  KPICard,
  SectionCard,
  StatusBadge,
  tableStyle,
  tdStyle,
  thStyle,
} from '../../components/common';
import { IconClipboardCheck, IconFileText, IconTrendUp, IconWallet } from '../../components/icons';
import type { ApInvoice, JournalEntry, JournalLine, Supplier } from '../../types/domain';

function agingBucket(dueDate: string): string {
  const days = Math.floor((Date.now() - new Date(dueDate).getTime()) / 86400000);
  if (days <= 30) return '0–30 hari';
  if (days <= 60) return '31–60 hari';
  if (days <= 90) return '61–90 hari';
  return '>90 hari';
}

const AGING_ORDER = ['0–30 hari', '31–60 hari', '61–90 hari', '>90 hari'];
const AGING_COLOR: Record<string, string> = {
  '0–30 hari': 'var(--alex-primary)',
  '31–60 hari': 'var(--alex-success)',
  '61–90 hari': 'var(--alex-warning)',
  '>90 hari': 'var(--alex-danger)',
};

/**
 * Finance & Accounting dashboard — every KPI and panel here reads a real
 * table (ap_invoices/journal_entries/journal_lines — migration 0036,
 * FINANCE + TENANT_OWNER only). Cash Balance / Payments Today were REMOVED
 * from this dashboard entirely: no real bank/cash-transaction source
 * exists anywhere in this schema (BLOCKED_BY_SOURCE), and the owner's
 * instruction is to remove a fabricated number rather than keep showing
 * fake billions of rupiah. Trial Balance / P&L (FASE 10, migration 0044)
 * IS real now — fn_get_trial_balance aggregates real posted journal_lines
 * — the KPI card links out to /app/finance/reports rather than duplicating
 * that computation here.
 */
export default function FinanceDashboard() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [invoices, setInvoices] = useState<ApInvoice[] | null>(null);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [donutSelected, setDonutSelected] = useState<number | null>(null);
  const [selectedJournal, setSelectedJournal] = useState<JournalEntry | null>(null);

  const canReadAp = hasPermission('ap_invoice', 'read');
  const canReadJournal = hasPermission('journal_entry', 'read');
  const canReadSupplier = hasPermission('supplier', 'read');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      canReadAp ? listApInvoices(activeBranchId ?? undefined) : Promise.resolve(null),
      canReadSupplier ? listSuppliers() : Promise.resolve([]),
      canReadJournal ? listJournalEntries(10, activeBranchId ?? undefined) : Promise.resolve(null),
    ])
      .then(([inv, sup, je]) => {
        if (cancelled) return;
        setInvoices(inv);
        setSuppliers(sup);
        setJournalEntries(je);
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [canReadAp, canReadSupplier, canReadJournal, activeBranchId]);

  if (loading) return <DashboardSkeleton />;
  if (error) return <ErrorNote text={error} />;

  const now = Date.now();
  const openInvoices = invoices ? invoices.filter((i) => i.status !== 'PAID' && i.status !== 'VOID') : [];
  const openTotal = openInvoices.reduce((sum, i) => sum + i.amount, 0);
  const agingTotals: Record<string, number> = {};
  for (const inv of openInvoices) {
    const bucket = agingBucket(inv.due_date);
    agingTotals[bucket] = (agingTotals[bucket] ?? 0) + inv.amount;
  }
  const overdue90 = agingTotals['>90 hari'] ?? 0;
  const postedJournals = journalEntries ? journalEntries.filter((j) => j.status === 'POSTED').length : 0;
  const agingSegments = AGING_ORDER.filter((b) => agingTotals[b] > 0).map((b) => ({ value: agingTotals[b], color: AGING_COLOR[b], label: b }));
  const supplierName = (id: string) => suppliers.find((s) => s.supplier_id === id)?.name ?? id;
  const dueSoonInvoices = openInvoices
    .filter((inv) => new Date(inv.due_date).getTime() - now >= 0 && new Date(inv.due_date).getTime() - now <= 30 * 86400000)
    .sort((a, b) => a.due_date.localeCompare(b.due_date));

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--alex-card-gap)', marginBottom: 'var(--alex-card-gap)' }}>
        {canReadAp ? (
          <>
            <KPICard label="AP Invoice Open" value={`Rp ${openTotal.toLocaleString('id-ID')}`} hint={`${openInvoices.length} invoice`} icon={<IconFileText size={16} />} tone="warning" />
            <KPICard label="Jatuh Tempo >90 Hari" value={`Rp ${overdue90.toLocaleString('id-ID')}`} icon={<IconClipboardCheck size={16} />} tone={overdue90 > 0 ? 'danger' : 'neutral'} />
          </>
        ) : (
          <KPICard label="AP Invoice Open" value="—" hint="Tanpa izin" icon={<IconFileText size={16} />} tone="warning" />
        )}
        {canReadJournal && <KPICard label="Jurnal Terposting" value={postedJournals} icon={<IconTrendUp size={16} />} tone="primary" />}
        <KPICard label="Trial Balance / Laba Rugi" value="Lihat Laporan" hint="Migrasi 0044 — dihitung dari jurnal nyata" icon={<IconWallet size={16} />} tone="primary" onClick={() => navigate('/app/finance/reports')} />
      </div>

      <div className="alex-grid-8-4" style={{ marginBottom: 'var(--alex-card-gap)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          <SectionCard title="Invoice AP Terbuka">
            {!canReadAp && <EmptyState text="Tidak ada izin ap_invoice.read." variant="compact" />}
            {canReadAp && openInvoices.length === 0 && <EmptyState text="Tidak ada invoice AP terbuka." variant="compact" />}
            {canReadAp && openInvoices.length > 0 && (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>No. Invoice</th>
                    <th style={thStyle}>Supplier</th>
                    <th style={thStyle}>Jatuh Tempo</th>
                    <th style={thStyle}>Jumlah</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {openInvoices.slice(0, 8).map((inv) => (
                    <tr key={inv.invoice_id}>
                      <td style={tdStyle}>{inv.invoice_no}</td>
                      <td style={tdStyle}>{canReadSupplier ? supplierName(inv.supplier_id) : inv.supplier_id}</td>
                      <td style={tdStyle}>{inv.due_date}</td>
                      <td style={tdStyle}>Rp {inv.amount.toLocaleString('id-ID')}</td>
                      <td style={tdStyle}>
                        <StatusBadge status={inv.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>

          <SectionCard title={canReadJournal ? 'Jurnal Terbaru' : 'Jurnal Terbaru'}>
            {!canReadJournal && <EmptyState text="Tidak ada izin journal_entry.read." variant="compact" />}
            {canReadJournal && (!journalEntries || journalEntries.length === 0) && <EmptyState text="Belum ada jurnal terposting." variant="compact" />}
            {canReadJournal && journalEntries && journalEntries.length > 0 && (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Tanggal</th>
                    <th style={thStyle}>No. Jurnal</th>
                    <th style={thStyle}>Deskripsi</th>
                    <th style={thStyle}>Sumber</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {journalEntries.map((je) => (
                    <ClickableRow key={je.journal_id} onClick={() => setSelectedJournal(je)}>
                      <td style={tdStyle}>{je.entry_date}</td>
                      <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{je.journal_no}</td>
                      <td style={tdStyle}>{je.description ?? '—'}</td>
                      <td style={tdStyle}>{je.source_type}</td>
                      <td style={tdStyle}>
                        <StatusBadge status={je.status} />
                      </td>
                    </ClickableRow>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--alex-card-gap)' }}>
          <SectionCard title="Aging Hutang (AP)">
            {!canReadAp && <EmptyState text="Tidak ada izin ap_invoice.read." variant="compact" />}
            {canReadAp && agingSegments.length === 0 && <EmptyState text="Tidak ada AP invoice terbuka." variant="compact" />}
            {canReadAp && agingSegments.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <DonutChart segments={agingSegments} size={92} thickness={14} selected={donutSelected} onSelectedChange={setDonutSelected} />
                <div style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-secondary)', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {agingSegments.map((seg, i) => (
                    <button
                      key={seg.label}
                      onClick={() => setDonutSelected(donutSelected === i ? null : i)}
                      className="alex-row-clickable"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        border: 'none',
                        background: donutSelected === i ? 'var(--alex-surface-subtle)' : 'none',
                        padding: '3px 6px',
                        borderRadius: 'var(--alex-control-radius)',
                        fontWeight: donutSelected === i ? 700 : 500,
                        color: 'var(--alex-text-secondary)',
                        textAlign: 'left',
                      }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: seg.color, display: 'inline-block', flexShrink: 0 }} />
                      {seg.label} · Rp {seg.value.toLocaleString('id-ID')}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Jatuh Tempo Segera (≤30 hari)">
            {!canReadAp && <EmptyState text="Tidak ada izin ap_invoice.read." variant="compact" />}
            {canReadAp && dueSoonInvoices.length === 0 && <EmptyState text="Tidak ada invoice jatuh tempo dalam 30 hari." variant="compact" />}
            {canReadAp && dueSoonInvoices.length > 0 && (
              <table style={tableStyle}>
                <tbody>
                  {dueSoonInvoices.slice(0, 5).map((inv) => (
                    <tr key={inv.invoice_id}>
                      <td style={tdStyle}>{inv.invoice_no}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--alex-text-secondary)' }}>{inv.due_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </SectionCard>
        </div>
      </div>

      <JournalDetailDrawer journal={selectedJournal} onClose={() => setSelectedJournal(null)} />
    </div>
  );
}

function JournalDetailDrawer({ journal, onClose }: { journal: JournalEntry | null; onClose: () => void }) {
  const [lines, setLines] = useState<JournalLine[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!journal) return;
    setLines(null);
    setError(null);
    listJournalLines([journal.journal_id])
      .then(setLines)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [journal]);

  return (
    <Drawer open={!!journal} onClose={onClose} title={journal ? `Jurnal ${journal.journal_no}` : 'Detail Jurnal'}>
      {journal && (
        <div>
          <DrawerField label="No. Jurnal" value={journal.journal_no} />
          <DrawerField label="Status" value={<StatusBadge status={journal.status} />} />
          <DrawerField label="Tanggal" value={journal.entry_date} />
          <DrawerField label="Sumber" value={journal.source_type} />
          <DrawerField label="Deskripsi" value={journal.description ?? '—'} />
          <DrawerField label="Diposting" value={journal.posted_at ? new Date(journal.posted_at).toLocaleString('id-ID') : 'Belum diposting'} />

          <div style={{ marginTop: 14, fontSize: 'var(--alex-fs-caption)', fontWeight: 700, color: 'var(--alex-text-secondary)', marginBottom: 6 }}>Baris Jurnal</div>
          {error && <ErrorNote text={error} />}
          {!error && lines === null && <p style={{ fontSize: 'var(--alex-fs-caption)', color: 'var(--alex-text-faint)' }}>Memuat...</p>}
          {lines && lines.length === 0 && <EmptyState text="Tidak ada baris pada jurnal ini." variant="compact" />}
          {lines && lines.length > 0 && (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Deskripsi</th>
                  <th style={thStyle}>Debit</th>
                  <th style={thStyle}>Kredit</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.line_id}>
                    <td style={tdStyle}>{l.description ?? '—'}</td>
                    <td style={tdStyle}>{l.debit > 0 ? `Rp ${l.debit.toLocaleString('id-ID')}` : '—'}</td>
                    <td style={tdStyle}>{l.credit > 0 ? `Rp ${l.credit.toLocaleString('id-ID')}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </Drawer>
  );
}
